import { consoleLogger, systemClock } from "../infra/adapters";
import { audio } from "../audio/audio";
import { GroundMesh } from "../render/groundMesh";
import { HubScene } from "../render/hubScene";
import { FoliageView } from "../render/views/foliage";
import { BaseSkin } from "../render/views/baseSkin";
import { WornSkins } from "../render/skins/wornSkins";
import { hubAssetNeeds, levelAssetInput, levelAssetNeeds } from "../render/assetNeeds";
import { showArmoury } from "../ui/armoury/armoury";
import { showSettings } from "../ui/settings";
import { showDebugMenu } from "../ui/debugMenu";
import { showStyleGuide } from "../ui/styleguide";
import { HubView } from "../ui/hub/hubView";
import { CampaignMap } from "../ui/hub/campaignMap";
import { setContinueInfo } from "../ui/hub/battleCard";
import { guide } from "../ui/guide/guide";
import type { GameServices, Nav, Screens } from "./context";
import type { MetaFlow } from "./metaFlow";
import { DEBUG } from "./debug";
import { sweepLevelUi } from "../level/levelUiScope";

/** The controller's debug surface (`window.__td.guide` with ?debug): show one step now, by id. Not in the contract. */
interface GuideDebug { show?(id: string): void }

/**
 * Part of the composition root: the hub (the lobby screen and its 3D scene), the campaign map over it, and the asset
 * sets both keep loaded — the hub's own, and the next level's, preloaded while the hub sits idle.
 */
export class HubFlow {
  private preloadTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  /** Two or three frames of the hub (its shaders compile on the first), then the downloads may start. */
  private static readonly PreloadDelayMs = 300;

  constructor(private readonly g: GameServices, private readonly screens: Screens, private readonly meta: MetaFlow, private readonly nav: Nav) {
    // A fortress skin with its own model (the Dragonlord) is another GLB, not a material: the hub is built again with it.
    WornSkins.onFortressModel = () => { if (this.screens.hub) void this.showMenu(); };
  }

  /** The level PLAY starts: the first one not yet cleared (the hub's battle card shows the same). */
  nextLevel(): number { return Math.min(this.g.catalog.levelCount, this.g.catalog.highestLevelCleared + 1); }

  /**
   * The hub's HQ set: the fortress and the owned towers at the tiers the hub stands them at, the current biome's
   * scenery, and the next tower to unlock at tier 1 — the hub stands its ghost on the next free stand (hubGhost.ts),
   * and a model the set did not list would be released under it as the hub came up.
   */
  hubAssetInput(): Parameters<typeof hubAssetNeeds>[0] {
    const { balance, catalog } = this.g;
    const owned = catalog.ownedTowers(), levels = catalog.towerLevels();
    const base = balance.towers.findIndex((t) => t.isBase);
    const next = balance.towers.filter((t) => !t.isBase && !owned[t.index]).sort((a, b) => a.unlockLevel - b.unlockLevel)[0];
    return {
      baseTier: HubScene.tierFor(levels[base]),
      towers: [
        ...balance.towers.filter((t) => !t.isBase && owned[t.index]).map((t) => ({ id: t.id, tier: HubScene.tierFor(levels[t.index]) })),
        ...(next ? [{ id: next.id, tier: 1 }] : []),
      ],
      biome: FoliageView.biome, skin: BaseSkin.current(), groundMesh: GroundMesh.available,
    };
  }

  /**
   * While the title and the hub sit idle, the next level's models load behind them (the lobby's perf slice), so PLAY
   * opens the level without waiting on the network. The hub waits `PreloadDelayMs` first — its own first frames come
   * first — and the hub keeps whatever of this set is already in (buildMenu). It only ever adds models: the terrain
   * maps of another biome (~110 MiB decoded) are never loaded on top of the hub's (startLevel takes the hub down
   * before it loads those, clearForLoad), and neither are that biome's props, so for
   * a level in the next chapter only its towers, enemies and road kit come early. Whatever is in flight when PLAY is
   * tapped is shared with the level's own load (AssetLibrary.ensure joins it); a level picked on the map instead
   * releases what it does not use once it is built (releaseAssets).
   */
  private preloadNextLevel(): void {
    clearTimeout(this.preloadTimer);
    if (!this.screens.lib || this.g.keepAllHq) return;
    this.preloadTimer = setTimeout(() => {
      if (!this.screens.hubScene || this.screens.level) return;   // the hub is already gone
      this.preloadNow();
    }, HubFlow.PreloadDelayMs);
  }

  preloadNow(): void {
    const set = this.nextLevelSet();
    if (set) void this.screens.lib!.ensure(set).catch(() => undefined);
  }

  /** The models the preload fetches for the next level (null when every model stays loaded anyway). */
  private nextLevelSet(): Set<string> | null {
    const lib = this.screens.lib;
    if (!lib || this.g.keepAllHq) return null;
    // the hub's own biome: for a level in it that is the level's, and for one in the next chapter its props wait
    const input = levelAssetInput(this.g.balance, this.nextLevel(), this.g.catalog.ownedTowers(), FoliageView.biome, BaseSkin.current(), GroundMesh.available);
    return levelAssetNeeds(input, lib.shippedNames);
  }

  /** Unloads the HQ models (and other biomes' terrain maps) the next screen does not use, once the old one is gone. */
  releaseAssets(keep: ReadonlySet<string>, biome: Parameters<typeof GroundMesh.release>[0]): void {
    const lib = this.screens.lib;
    if (this.g.keepAllHq || !lib) return;
    const gone = lib.release(keep);
    const maps = GroundMesh.release(biome);
    if (DEBUG && (gone.length || maps.length)) consoleLogger.log("info", `hq: released ${gone.length} models${maps.length ? ` and the ${maps.join(", ")} terrain maps` : ""}; ${lib.hqCount} loaded`);
  }

  showMenu(): void | Promise<void> {
    this.nav.offerUpdate();
    const lib = this.screens.lib;
    const needs = lib && !this.g.keepAllHq ? hubAssetNeeds(this.hubAssetInput(), lib.shippedNames) : null;
    // a tower unlocked by the level just won is not loaded yet: fetch it before the hub stands it up
    // A level started while those loaded (PLAY on a hub still up) has the screen now: the late hub is not built over it.
    if (needs && [...needs].some((n) => !lib!.has(`hq:${n}`))) return lib!.ensure(needs).then(() => { if (!this.screens.level) this.buildMenu(needs); });
    this.buildMenu(needs);
  }

  private buildMenu(needs: ReadonlySet<string> | null): void {
    const { catalog, keeper, save, saves, ui } = this.g, s = this.screens;
    this.g.wakeLock.hold(false);                               // the hub may dim and lock as usual
    audio.music("hub");
    this.closeScreens();
    // The hub is never built under a live level's HUD (M32: the thumb cluster drawn over the Achievements sheet): a
    // level still up here is taken down, and any level node a failed teardown left in the UI layer is swept.
    if (s.level) { console.warn("[hub] a level was still up as the hub was built: taken down"); const lv = s.level; s.level = null; lv.dispose(); }
    const swept = sweepLevelUi(ui);
    if (swept > 0) console.warn(`[hub] swept ${swept} level UI node(s) left behind`);
    // Daily Orders roll over to today (and the check-in notes the visit) before anything below counts towards them.
    catalog.orders.refresh(systemClock.utcNowSeconds(), catalog.highestLevelCleared);
    catalog.checkin.visit(systemClock.utcNowSeconds());
    this.meta.persist();
    const now = systemClock.utcNowSeconds();
    const done = catalog.profile.claimCompleted(now);         // finished while the game was closed: the away card says so
    if (done.length > 0) this.meta.persist();
    setContinueInfo(keeper.continueInfo());
    s.hubScene = new HubScene(this.g.rig, this.g.balance, catalog.ownedTowers(), catalog.towerLevels());
    s.hub = new HubView(ui, catalog, () => systemClock.utcNowSeconds(), {
      onStart: (level) => this.nav.requestStart(level),
      onChange: () => this.meta.persist(),
      onCampaign: () => this.showCampaign(),
      // The Gear tab opens the Armoury, scoped to the next level and the owned roster (its readiness dock).
      onInventory: () => showArmoury(ui, {
        B: this.g.balance, profile: catalog.profile, save, level: catalog.nextLevel, roster: catalog.ownedTowers(),
        persist: () => { this.meta.persist(); s.hub?.rerender(); },
        battle: () => s.hub?.playNext(),
        openTowers: () => s.hub?.openTab("towers"),
      }),
      onSettings: () => showSettings(ui, this.nav.settingsActions()),
      seen: {
        towers: () => save.seenTowers,
        markTowers: (ids) => { save.seenTowers = [...new Set(ids)]; saves.save(save); },
        gearSeenUpTo: () => save.gearSeenUpTo,
      },
      onTick: () => { void keeper.hubTick(systemClock.utcNowSeconds()); this.meta.announceOrders(); },
      onDebug: DEBUG ? () => showDebugMenu(ui, {
        catalog,
        currentLevel: () => s.level,
        persist: () => this.meta.persist(),
        exportSave: () => { this.meta.persist(); return JSON.stringify(save); },
        shiftClock: (seconds) => { for (const j of catalog.profile.jobs) j.endAt += seconds; },
        refresh: () => s.hub?.rerender(),
        styleGuide: () => showStyleGuide(ui, this.g.balance, catalog.profile),
        resetGuide: () => this.nav.resetGuide(),
        showGuideStep: guide() ? (id) => (guide() as GuideDebug | null)?.show?.(id) : null,
      }) : null,
    }, s.hubScene, this.g.version);
    // the next level's set stays: the title (or an earlier hub) may have preloaded it already
    if (needs) this.releaseAssets(new Set([...needs, ...(this.nextLevelSet() ?? [])]), FoliageView.biome);
    this.preloadNextLevel();
    void keeper.enterHub(done, now, () => s.level !== null);
    this.meta.announceOrders();
  }

  /** Campaign map (P1.10.4) over the hub scene; the hub UI hides while it is open, and the scene under it stops rendering. */
  private showCampaign(): void {
    const s = this.screens, catalog = this.g.catalog;
    s.hub?.dispose();
    s.hub = null;
    s.hubScene?.setPaused(true);
    s.campaign = new CampaignMap(this.g.ui, catalog, { onBack: () => void this.nav.showMenu(), onBattle: (level) => this.nav.requestStart(level),
      onClaimStarChest: (chapter) => { const claim = catalog.claimStarChest(chapter, this.g.save.playerId, systemClock.utcNowSeconds()); if (claim) this.meta.persist(); return claim; },
      now: () => systemClock.utcNowSeconds() });
  }

  closeScreens(): void {
    const s = this.screens;
    s.hub?.dispose(); s.hub = null;
    s.campaign?.dispose(); s.campaign = null;
    s.hubScene?.dispose(); s.hubScene = null;
  }
}
